import { useCallback } from 'react'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { trackClientEvent } from 'analytics/analytics'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

export type MegaMenuSection =
  | 'category'
  | 'subcategory'
  | 'brand'
  | 'travel_essential'
  | 'free_shipping'
  | 'triple_points'
  | 'brands_az'
  | 'view_all_brands'

interface TrackingItem {
  id: string
  name: string
}

export function trackMegaMenuOpen(section: MegaMenuSection): void {
  trackClientEvent({
    subject: 'mega_menu',
    action: `${section}_open`,
    category: 'navigation',
    type: 'interaction',
  })
}

function trackMegaMenuClick(
  section: MegaMenuSection,
  item: TrackingItem,
  position?: number,
): void {
  trackClientEvent({
    subject: 'mega_menu',
    action: `${section}_click`,
    category: 'navigation',
    type: 'interaction',
    context: [
      comluxgroup.createListItem_1_0_0({
        listName: `mega_menu_${section}`,
        listInstance: `${item.id}:${item.name}`,
        itemPosition: (position ?? 0) + 1,
      }),
    ],
  })
}

export function trackMegaMenuLinkClick(section: MegaMenuSection): void {
  trackClientEvent({
    subject: 'mega_menu',
    action: `${section}_click`,
    category: 'navigation',
    type: 'interaction',
  })
}

export function useMegaMenuTracking(section: MegaMenuSection, onClose?: () => void) {
  return useCallback(
    (item: TrackingItem, position?: number) => {
      trackMegaMenuClick(section, item, position)
      onClose?.()
    },
    [section, onClose],
  )
}

export function useTrackedMenuOpen(section: MegaMenuSection) {
  return useCallback((open: boolean) => {
    if (open) {
      trackMegaMenuOpen(section)
    }
  }, [section])
}

export function useDrawerTracking(section: MegaMenuSection, onCloseAll?: () => void) {
  const modalElement = useModalElementContext()
  const onClose = useCallback(() => {
    modalElement.resolve()
    onCloseAll?.()
  }, [modalElement, onCloseAll])
  const trackItemClick = useMegaMenuTracking(section, onClose)
  return { onClose, trackItemClick }
}
